import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Form from "src/components/base/form";
import { useForm, useStore } from "@tanstack/react-form";
import React, { Suspense, useState } from "react";
import { notify } from "src/components/base/toast";
import { Description, ErrorMessage, Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Divider } from "src/components/base/divider";
import { Button, PrimaryButton } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";
import { Switch, SwitchField } from "src/components/base/switch";
import BlockUnsavedChanges from "src/components/base/unsaved-changes";
import { ArrowPathIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import GenerateScimTokenDialog from "src/components/dialogs/generate-scim-token";

/**
 * The properties for {@link SettingScim}
 */
export type SettingScimProps = {};

/**
 * Setting page to configure SCIM
 */
function SettingScim(props: SettingScimProps) {
    const [t] = useTranslation("admin-settings");
    const [tg] = useTranslation();

    const router = useRouter();
    const data = Route.useLoaderData();
    const { tenantId } = Route.useParams();

    const [showToken, setShowToken] = useState(false);
    const [openGenerateRandomKey, setOpenGenerateRandomKey] = useState(false);

    const form = useForm({
        defaultValues: {
            enabled: data.enabled,
            token: data.token,
            entra_workaround: data.entra_workaround,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (value.enabled && value.token.length === 0) {
                    return { fields: { token: t("error.token-empty") } };
                }

                await ApiInternal.settings.setScim(tenantId, {
                    enabled: value.enabled,
                    token: !isDirty ? undefined : value.token,
                    entra_workaround: value.entra_workaround,
                });

                notify.success(tg("toast.saved-successfully"));
                router.invalidate();
                form.reset();
            },
        },
    });
    const isDirty = useStore(form.store, (store) => store.isDirty);

    return (
        <div className={"flex flex-col justify-start gap-4"}>
            <BlockUnsavedChanges condition={isDirty} />
            <Form className={"flex max-w-lg flex-col gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field name={"enabled"}>
                            {(fieldApi) => (
                                <SwitchField className={"mb-4"}>
                                    <Label>{t("label.enable-scim")}</Label>
                                    <Switch
                                        color={"blue"}
                                        checked={fieldApi.state.value}
                                        onChange={fieldApi.handleChange}
                                    />
                                    <Description>{t("description.enable-scim")}</Description>
                                </SwitchField>
                            )}
                        </form.Field>
                        <form.Field name={"token"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.scim-token")}</Label>
                                    <Description>{t("description.scim-token")}</Description>
                                    <div className={"flex flex-row gap-2"}>
                                        <Input
                                            type={showToken ? "text" : "password"}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            maxLength={255}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                        />
                                        <Button onClick={() => setShowToken(!showToken)} outline={true}>
                                            {showToken ? <EyeIcon /> : <EyeSlashIcon />}
                                        </Button>
                                        <Button outline={true} onClick={() => setOpenGenerateRandomKey(true)}>
                                            <ArrowPathIcon></ArrowPathIcon>
                                        </Button>
                                    </div>
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage key={err}>{err}</ErrorMessage>
                                    ))}
                                    <Suspense>
                                        <GenerateScimTokenDialog
                                            isOpen={openGenerateRandomKey}
                                            onClose={() => setOpenGenerateRandomKey(false)}
                                            onSubmit={() => {
                                                fieldApi.handleChange(randomToken());
                                                setOpenGenerateRandomKey(false);
                                            }}
                                        />
                                    </Suspense>
                                </Field>
                            )}
                        </form.Field>
                        <form.Field name={"entra_workaround"}>
                            {(fieldApi) => (
                                <SwitchField className={"mb-4"}>
                                    <Label>{t("label.entra-workaround")}</Label>
                                    <Switch
                                        color={"blue"}
                                        checked={fieldApi.state.value}
                                        onChange={fieldApi.handleChange}
                                    />
                                    <Description>{t("description.entra-workaround")}</Description>
                                </SwitchField>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
                <Divider />
                <div className={"flex justify-end gap-3"}>
                    <Button plain={true} disabled={!isDirty} onClick={() => form.reset()}>
                        {t("button.reset")}
                    </Button>
                    <PrimaryButton type="submit">{t("button.save")}</PrimaryButton>
                </div>
            </Form>
        </div>
    );
}

/**
 * Generates a random token
 *
 * @returns random base64 string
 */
function randomToken() {
    const array = new Uint8Array(24);
    window.crypto.getRandomValues(array);
    return array.toBase64({ omitPadding: true });
}

export const Route = createFileRoute("/_admin/a/tenants/$tenantId/_tenant/scim")({
    component: SettingScim,
    loader: async ({ params }) => {
        const settings = await ApiInternal.settings.getScim(params.tenantId);
        if (settings.token.length === 0) {
            settings.token = randomToken();
            await ApiInternal.settings.setScim(params.tenantId, settings);
        }
        return settings;
    },
});
